/* eslint-disable max-len */
import React, { useCallback, useEffect, useState } from 'react'
import { Field, Form, withFormik } from 'formik'
import _map from 'lodash/map'
import _get from 'lodash/get'
import _find from 'lodash/find'
import _reduce from 'lodash/reduce'
import _forEach from 'lodash/forEach'
import _values from 'lodash/values'
import _isEmpty from 'lodash/isEmpty'
import _isArray from 'lodash/isArray'
import _filter from 'lodash/filter'

import { Element, Events, scroller } from 'react-scroll'

import { fetchSeatsIoData } from '../../../../_common/core/http_services'
import { showAxiosError } from '../../../utils/messenger'

import Button from '../../../_library/Button'
import Select from '../../../_library/Select'
import LoadingBar from '../../../_library/LoadingBar'
import Card from '../../../_library/Card'
import TagsField2 from '../../../_library/TagsField2'
import { RadioGroup, Radio } from '../../../_library/RadioGroup'
import { OverlayTrigger } from '../../../_library/OverlayTrigger'

import RegularTickets from './RegularTickets'
import { SeatsIo } from './SeatsIo'
import { asyncComponent } from '../../../hoc'
import { SimpleField } from '../../../formik/Fields'
import { useGuestTickets } from './useGuestTickets'
import { validateTicket } from './validate'
import ConfirmModal from '../../../_library/ConfirmModal'
import { getPermissions } from '../../../_theme/utils'
import { getScrollToGuestTicketFormElement } from './scrollTo'

const SeatMap = asyncComponent(() => import('./SeatMap'))

const options_quantity = _map(new Array(40), (_, i) => ({ value: i + 1, label: i + 1 }))

const GuestTicketForm = props => {
  const {
    user,
    event,
    tickets,
    tables,
    addons,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    cardsStatus,
    getSeatDataLoading,
    seatDataChecked,
    isSubmitting,
    submitLabel,
    resetForm,
    ticketTypes,
    handleSubmit,
  } = props

  const role = event?.self.role
  const permissions = getPermissions(role)
  const isAdminOrOwner = permissions.isAdminOrOwner()

  const [seatsIoData, setSeatsIoData] = useState()
  const [isLoadingSeatsIoData, setIsLoadingSeatsIoData] = useState(false)
  const [hasMapConfigurations, setHasMapConfigurations] = useState(false)
  const [isShowQuantity, setShowQuantity] = useState(false)
  const [openOverrideModal, setOverrideModal] = useState(false)
  const [quantityError, setQuantityError] = useState(null)
  const [alertModalOpen, setAlertModalOpen] = useState(false)

  const {
    hasRegularTickets,
    hasRegularTicketsOnSeatMap,
    hasRegularTicketsDirectSales,
    regularTicketsNotConnectedToSeatMap,
    hasTables,
    hasTablesOnSeatMap,
    hasTablesDirectSales,
    tablesNotConnectedToSeatMap,
    isSeatMapEvent,
    isTableSeatMap,
    isSeatsIo,
  } = useGuestTickets({
    event,
    tickets,
    tables,
    seatMapConnectedIds: Array.from(
      new Set(_reduce(_values(ticketTypes), (curr, next) => [...curr, ...next], [])),
    ),
  })

  const [selectedTab, setSelectedTab] = useState('regularTickets')

  useEffect(() => {
    setSelectedTab(
      isSeatMapEvent && !hasRegularTickets && (hasRegularTicketsOnSeatMap || hasTablesOnSeatMap)
        ? 'ticketsOnMap'
        : 'regularTickets',
    )
  }, [hasRegularTickets, hasRegularTicketsOnSeatMap, hasTablesOnSeatMap, isSeatMapEvent])

  useEffect(() => {
    const { id, seatProvider } = event

    const getSeatsIoData = async () => {
      try {
        const seatsIoDataResponse = await fetchSeatsIoData(id)
        setSeatsIoData(seatsIoDataResponse)
        setIsLoadingSeatsIoData(false)
        setHasMapConfigurations(true)
      } catch (error) {
        showAxiosError(error)
        setIsLoadingSeatsIoData(false)
      }
    }

    if (seatProvider === 'seats_io') {
      getSeatsIoData()
    }
  }, [event])

  const processQuantity = useCallback(
    count => {
      const updatedTicketHolders = [...values.ticketHolders]
      const count_pre = values.ticketHolders.length
      if (count < count_pre) {
        for (let i = 0; i < count_pre - count; i++) {
          updatedTicketHolders.splice(count, 1)
        }
      }
      if (count > count_pre) {
        for (let i = 0; i < count - count_pre; i++) {
          updatedTicketHolders.push({
            first_name: '',
            last_name: '',
            add_ons: [],
          })
        }
      }

      setFieldValue('ticketHolders', updatedTicketHolders)
    },
    [setFieldValue, values.ticketHolders],
  )

  let filteredTimeSlotTickets = {}

  _forEach(tickets, (groupItem, key) => {
    if (_isArray(groupItem)) {
      if (filteredTimeSlotTickets[key] || !isNaN(+key)) {
        filteredTimeSlotTickets[`${key} - Slots`] = _filter(groupItem, { active: true })
      } else {
        filteredTimeSlotTickets[key] = _filter(groupItem, { active: true })
      }
    }
  })

  const hasAttendeeTags = event.attendeeTags && event.attendeeTags.length > 0

  let ticketTableLabel = 'Ticket Type'
  if (hasRegularTicketsDirectSales && hasTablesDirectSales) {
    ticketTableLabel = 'Ticket/Table Type'
  } else if (hasRegularTickets) {
    ticketTableLabel = 'Ticket Type'
  } else if (hasTables) {
    ticketTableLabel = 'Table Type'
  }

  const optionsTickets = [{ value: '', label: `Select ${ticketTableLabel}` }]
  _forEach(regularTicketsNotConnectedToSeatMap, ticketItem => {
    if (ticketItem.active) {
      const key = ticketItem.displayName || ticketItem.name
      optionsTickets.push({ value: ticketItem.id || key, label: key })
    }
  })

  if (isSeatMapEvent || isSeatsIo) {
    filteredTimeSlotTickets = _filter(filteredTimeSlotTickets, ticket => ticket.allowDirectPurchaseForSeatMap)
  }

  _forEach(filteredTimeSlotTickets, (ticket, key) => {
    optionsTickets.push({ value: ticket.id || key, label: ticket.displayName || key })
  })

  _forEach(tablesNotConnectedToSeatMap, tableItem => {
    if (tableItem.active) {
      const key = tableItem.displayName || tableItem.name
      optionsTickets.push({ value: tableItem.id || key, label: key })
    }
  })

  const optionsAddons = [{ value: '', label: 'Select Add On' }]
  _forEach(addons, addon => {
    if (!addon.isGroup) {
      const addonName = addon.addOnGroupName ? `${addon.addOnGroupName}: ${addon.name}` : addon.name
      optionsAddons.push({ value: addon.id, label: addonName })
    }
  })

  const handleChangeTicketTypeID = useCallback(
    e => {
      const v = e.target ? e.target.value : e

      if ((Number(v) || Number(v) === 0) && !v.includes('e')) {
        setFieldValue('selectedGroup', null)
        setFieldValue('selectedDay', '')
        setFieldValue('selectedTime', '')
        setFieldValue('ticketTypeID', v)
      } else {
        setFieldValue('ticketTypeID', null)
        if (values.selectedGroup !== v) {
          setFieldValue('selectedDay', '')
          setFieldValue('selectedTime', '')
        }
        setFieldValue('selectedGroup', v)
      }
    },
    [setFieldValue, values.selectedGroup],
  )

  const handleChangeQuantity = useCallback(
    e => {
      const v = e.target ? e.target.value : e
      setFieldValue('quantity', parseInt(v))
      setFieldTouched('quantity', true)
      processQuantity(v)
    },
    [processQuantity, setFieldTouched, setFieldValue],
  )

  const handleTicketTypeSelect = useCallback(
    e => {
      const value = e.target ? e.target.value : e

      if (isTableSeatMap) {
        const tableTypeOptions = []
        _map(value || [], item => {
          const tableType = _find(tables, tableItem => String(tableItem.id) === String(item))
          if (tableType) {
            setFieldValue('tableTypeID', tableType.id)
            tableTypeOptions.push({
              id: tableType.id,
              value: tableType.id,
              label: tableType.displayName,
            })
          }
        })
        setFieldValue('tableTypeOptions', tableTypeOptions)
      } else {
        const ticketTypeOptions = []
        _map(value || [], item => {
          const ticketType = _find(tickets, ticketItem => String(ticketItem.id) === String(item))
          if (ticketType) {
            ticketTypeOptions.push({
              id: ticketType.id,
              value: ticketType.id,
              label: ticketType.displayName,
            })
          }
        })
        setFieldValue('ticketTypeOptions', ticketTypeOptions)
        handleChangeTicketTypeID(ticketTypeOptions[0].id)
      }
    },
    [handleChangeTicketTypeID, isTableSeatMap, setFieldValue, tables, tickets],
  )

  const handleArrayChange2 = useCallback(
    (e, index, aindex, field) => {
      const { value } = e.target
      const ticketHolders = [...values.ticketHolders]

      ticketHolders[index].add_ons[aindex][field] = value
      setFieldValue('ticketHolders', ticketHolders)
    },
    [setFieldValue, values.ticketHolders],
  )

  useEffect(() => {
    const allTickets = Array.isArray(tickets)
      ? tickets
      : Object.values(tickets).reduce((curr, next) => {
          if (Array.isArray(next)) {
            return curr.concat(next)
          }
          curr.push(next)
          return curr
        }, [])
    const selectedTicket = allTickets?.find?.(item => item.id === values.ticketTypeID)
    const selectedTable = tables.find(item => item.id === (values.tableTypeID || values.ticketTypeID))
    const stockValue = selectedTicket?.stock || selectedTable?.stock || 0

    if (selectedTicket || selectedTable) {
      if (values.quantity > stockValue) {
        if (isAdminOrOwner || user?.isSuperAdmin) {
          setQuantityError(
            'Current remaining stock is lower than the amount of tickets being issued. Please select the option to override stock levels.',
          )
        } else {
          setAlertModalOpen(true)
        }
      } else {
        setQuantityError(null)
      }
    }

    if (!values.ticketTypeID && !values.tableTypeID) {
      setQuantityError(null)
    }
  }, [values.quantity, values.ticketTypeID, values.tableTypeID, tickets, tables, isAdminOrOwner, user])

  useEffect(() => {
    if (!props.dirty && !props.isSubmitting) {
      setOverrideModal(false)
    }
  }, [props.dirty, props.isSubmitting])

  useEffect(() => {
    if (!_isEmpty(props.errors) && props.isSubmitting) {
      Events.scrollEvent.register('end', () => {
        Events.scrollEvent.remove('end')
      })
  
      const scrollTo = getScrollToGuestTicketFormElement(props.errors)
  
      scroller.scrollTo(scrollTo, {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: -120,
      })
    }
  }, [props.isSubmitting, props.errors])

  return (
    <div>
      <ConfirmModal
        isOpen={openOverrideModal}
        header="Confirm Stock Override"
        content={
          <div>
            Selecting this option will allow you to issue more tickets than are available in the remaining
            stock.
          </div>
        }
        actions={[
          {
            value: 'ok',
            label: 'Confirm',
            className: 'btn btn-success btn-shadow',
            disabled: props.isSubmitting,
          },
          {
            value: 'cancel',
            label: 'Cancel',
            className: 'btn btn-cancel',
          },
        ]}
        classNames={{
          container: 'modal-dialog schedule-modal-dialog',
        }}
        onAction={action => {
          if (action === 'ok') {
            handleSubmit(values, { ...props })
          } else {
            setOverrideModal(false)
          }
        }}
      />
      <ConfirmModal
        isOpen={alertModalOpen}
        header="Warning"
        content={
          <div>
            Current remaining stock is lower than the amount of tickets being issued. Please ask an
            Admin/Owner to reissue tickets after selecting the option to override stock levels.
          </div>
        }
        actions={[
          {
            value: 'cancel',
            label: 'Close',
            className: 'btn btn-cancel',
          },
        ]}
        classNames={{
          container: 'modal-dialog schedule-modal-dialog',
        }}
        onAction={action => {
          if (action === 'cancel') {
            setFieldValue('ticketTypeID', '')
            setFieldValue('tableTypeID', '')
            setFieldValue('quantity', 1)
            processQuantity(1)
            setAlertModalOpen(false)
          }
        }}
      />
      <Form>
        <Card
          title={'Send Guest Tickets'}
          className="guest-ticket-form"
          status={cardsStatus && cardsStatus.form}
        >
          {getSeatDataLoading || !seatDataChecked ? (
            <LoadingBar />
          ) : (
            <div>
              {hasRegularTickets && isSeatMapEvent && (
                <RadioGroup
                  name="fruit"
                  selectedValue={selectedTab}
                  onChange={tab => {
                    if (tab === selectedTab) return
                    setSelectedTab(tab)
                    processQuantity(1)
                    resetForm()
                  }}
                >
                  <Radio value="regularTickets" label="Regular Tickets" />
                  <Radio value="ticketsOnMap" label="Tickets on the Map" />
                </RadioGroup>
              )}
              <Element name="scroll-to-top" />
              {(hasRegularTicketsDirectSales || hasTablesDirectSales) && selectedTab === 'regularTickets' ? (
                <RegularTickets
                  values={values}
                  ticketTableLabel={ticketTableLabel}
                  optionsTickets={optionsTickets}
                  errors={errors}
                  touched={touched}
                  options_quantity={options_quantity}
                  filteredTimeSlotTickets={filteredTimeSlotTickets}
                  handleChangeTicketTypeID={handleChangeTicketTypeID}
                  handleChangeQuantity={handleChangeQuantity}
                  handleDayChange={e => {
                    const day = e.target.value
                    setFieldValue('selectedDay', day)
                    !day && setFieldValue('selectedTime', '')
                  }}
                  handleTimeChange={e => {
                    const time = e.target.value
                    const selectedArray = _find(
                      tickets,
                      (_, key) => key === values.selectedGroup || `${key} - Slots` === values.selectedGroup,
                    )
                    const selectedTicketType = _find(
                      selectedArray,
                      t => t.slotStartDate === values.selectedDay + ' ' + time,
                    )

                    setFieldValue('selectedTime', time)
                    setFieldValue('ticketTypeID', selectedTicketType ? selectedTicketType.id : null)
                  }}
                  handleChangeEmail={e => setFieldValue('email', e.target.value)}
                  handleChangeConfirmEmail={e => setFieldValue('confirmEmail', e.target.value)}
                  setFieldValue={setFieldValue}
                  quantityError={quantityError}
                  event={event}
                  user={user}
                />
              ) : null}

              {isSeatsIo ? (
                <SeatsIo
                  errors={errors}
                  touched={touched}
                  values={values}
                  handleChangeEmail={e => setFieldValue('email', e.target.value)}
                  handleChangeConfirmEmail={e => setFieldValue('confirmEmail', e.target.value)}
                  isShowQuantity={isShowQuantity}
                  options_quantity={options_quantity}
                  handleChangeQuantity={handleChangeQuantity}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  tickets={tickets}
                  tables={tables}
                  addons={addons}
                  optionsAddons={optionsAddons}
                  fetchSeatsIoDataResponse={seatsIoData}
                  fetchSeatsIoDataResponseLoading={isLoadingSeatsIoData}
                />
              ) : null}

              {isSeatMapEvent && selectedTab === 'ticketsOnMap' ? (
                <SeatMap
                  changeTicketType={handleTicketTypeSelect}
                  changeQuantity={handleChangeQuantity}
                  showQuantityField={value => {
                    setShowQuantity(value)
                    if (value) {
                      setFieldValue('quantity', 1)
                      processQuantity(1)
                    }
                  }}
                  event={event}
                  values={values}
                  touched={touched}
                  errors={errors}
                  ticketTypeOptions={isTableSeatMap ? values.tableTypeOptions : values.ticketTypeOptions}
                  isShowQuantity={isShowQuantity}
                  options_quantity={options_quantity}
                  handleSelect={option => handleChangeTicketTypeID(option.id)}
                  handleChangeEmail={e => setFieldValue('email', e.target.value)}
                  handleChangeConfirmEmail={e => setFieldValue('confirmEmail', e.target.value)}
                />
              ) : null}

              {_map(values.ticketHolders, (_, index) => (
                <div key={index} className="row">
                  <Element name={`scroll-to-ticket-holder-${index}`} />
                  <div className="col-xs-12">
                    <div className="section-title-sytle">
                      <label>Ticket Holder #{index + 1}</label>
                    </div>
                  </div>
                  <div className="col-xs-4 col-lg-5 col-12">
                    <Field
                      name={'ticketHolders[' + index + '].first_name'}
                      label="First Name"
                      value={_get(values, 'ticketHolders[' + index + '].first_name')}
                      error={_get(errors, 'ticketHolders[' + index + '].first_name')}
                      touched={_get(touched, 'ticketHolders[' + index + '].first_name')}
                      component={SimpleField}
                      onChange={(_, value) => {
                        const ticketHolders = [...values.ticketHolders]
                        ticketHolders[index].first_name = value
                        setFieldValue('ticketHolders', ticketHolders)
                      }}
                    />
                  </div>
                  <div className="col-xs-4 col-lg-5 col-12">
                    <Field
                      name={'ticketHolders[' + index + '].last_name'}
                      label="Last Name"
                      value={_get(values, 'ticketHolders[' + index + '].last_name')}
                      error={_get(errors, 'ticketHolders[' + index + '].last_name')}
                      touched={_get(touched, 'ticketHolders[' + index + '].last_name')}
                      component={SimpleField}
                      onChange={(_, value) => {
                        const ticketHolders = [...values.ticketHolders]
                        ticketHolders[index].last_name = value
                        setFieldValue('ticketHolders', ticketHolders)
                      }}
                    />
                  </div>
                  {addons.length > 0 && (
                    <div className="col-xs-2 col-12">
                      <Button
                        className="btn btn-primary btn-shadow guest-ticket-form-ticketholder-add-addon-btn"
                        type="button"
                        onClick={() => {
                          const ticketHolders = [...values.ticketHolders]
                          ticketHolders[index].add_ons.push({
                            quantity: 1,
                            id: '',
                          })
                          setFieldValue('ticketHolders', ticketHolders)
                        }}
                      >
                        Attach Add-on
                      </Button>
                    </div>
                  )}
                  {_map(values.ticketHolders[index].add_ons, (addon, aindex) => (
                    <div key={aindex} className="col-xs-12 guest-ticket-form-ticketholder-addon">
                      <div className="row">
                        <div className="col-xs-4 col-lg-5 col-12">
                          <Select
                            id="addonId"
                            label={'Add On #' + (aindex + 1)}
                            options={optionsAddons}
                            value={_get(values, 'ticketHolders[' + index + '].add_ons[' + aindex + '].id')}
                            error={_get(errors, 'ticketHolders[' + index + '].add_ons[' + aindex + '].id')}
                            touched={_get(touched, 'ticketHolders[' + index + '].add_ons[' + aindex + '].id')}
                            onChange={e => handleArrayChange2(e, index, aindex, 'id')}
                            onBlur={e => handleArrayChange2(e, index, aindex, 'id')}
                          />
                        </div>
                        <div className="col-xs-4  col-lg-5 col-12">
                          <Select
                            id={'addon_quantity_' + index + '_' + aindex}
                            label="Quantity"
                            options={options_quantity}
                            value={_get(
                              values,
                              'ticketHolders[' + index + '].add_ons[' + aindex + '].quantity',
                            )}
                            error={_get(
                              errors,
                              'ticketHolders[' + index + '].add_ons[' + aindex + '].quantity',
                            )}
                            onChange={e => handleArrayChange2(e, index, aindex, 'quantity')}
                            onBlur={e => handleArrayChange2(e, index, aindex, 'quantity')}
                          />
                        </div>
                        <div className="col-xs-3 col-lg-2 col-12">
                          <Button
                            className="btn btn-danger btn-shadow guest-ticket-form-ticketholder-remove-addon-btn"
                            type="button"
                            onClick={() => {
                              const ticketHolders = [...values.ticketHolders]
                              ticketHolders[index].add_ons.splice(aindex, 1)
                              setFieldValue('ticketHolders', ticketHolders)
                            }}
                          >
                            <i className="fa fa-trash" />
                            <span>Delete</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {values.ticketHolders.length > 1 && index === 0 && (
                    <div className="col-xs-12 col-12">
                      <Button
                        className="btn btn-primary btn-shadow guest-ticket-form-ticketholder-copy"
                        type="button"
                        onClick={() => {
                          const ticketHolders = [...values.ticketHolders]

                          for (let i = 1; i < ticketHolders.length; i++) {
                            ticketHolders[i].first_name = ticketHolders[0].first_name
                            ticketHolders[i].last_name = ticketHolders[0].last_name
                          }
                          setFieldValue('ticketHolders', ticketHolders)
                        }}
                      >
                        Copy details to all other tickets
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              <div className="row">
                <div className="col-xs-12 col-12 ticket-holders-info">
                  <br />
                  <Field
                    name="notes"
                    label="Notes (optional)"
                    component={SimpleField}
                    onChange={(fieldName, value) => setFieldValue(fieldName, value)}
                  />
                </div>
              </div>
              {hasAttendeeTags && (
                <div className="row">
                  <div className="col-xs-12 col-12 ticket-holders-info">
                    <br />
                    <TagsField2
                      label="Attendee Tags"
                      name="tags"
                      value={values.tags}
                      tags={event.attendeeTags}
                      onChange={tags => setFieldValue('tags', tags)}
                    />
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-xs-12 col-12">
                  <br />
                  <OverlayTrigger
                    hideTooltip={isSeatsIo || !hasMapConfigurations}
                    placement="top"
                    id="issueTicketsButtonTooltip"
                    tooltip={<span>There are no Ticket Types connected to the Seat Map</span>}
                    delayShow={400}
                    delayHide={10}
                  >
                    <div className="display-inline-block">
                      <Button
                        className={
                          isSeatsIo || !hasMapConfigurations
                            ? 'btn btn-primary btn-shadow'
                            : 'btn btn-primary btn-shadow pointer-events-none'
                        }
                        type="button"
                        loading={isSubmitting}
                        disabled={
                          (isSeatsIo && !hasMapConfigurations) || (quantityError && !values.flagOverrideStock)
                        }
                        onClick={() => {
                          if (values.flagOverrideStock) {
                            props.validateForm(values).then(({ ticketHolders, ...res }) => {
                              if (_isEmpty(res) && !ticketHolders) {
                                setOverrideModal(true)
                              } else {
                                handleSubmit(values, { ...props })
                              }
                            })
                          } else {
                            handleSubmit(values, { ...props })
                          }
                        }}
                      >
                        {submitLabel || 'Issue Tickets'}
                      </Button>
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          )}
        </Card>
      </Form>
    </div>
  )
}

export const GuestTicketFormContainer = withFormik({
  mapPropsToValues: props => ({
    quantity: 1,
    first_name: '',
    last_name: '',
    email: '',
    confirmEmail: '',
    ticketTypeID: '',
    tableTypeID: '',
    ticketHolders: [
      {
        first_name: '',
        last_name: '',
        add_ons: [],
      },
    ],
    notes: '',
    tags: [],
    selectedGroup: '',
    selectedDay: '',
    selectedTime: '',
    isSeatMap: true,
    isSeatsIo: false,
    seat_io_tickets: [],
    ticketTypeOptions: null,
    seatMapType: props.event.seatMapType,
    tickets: props.tickets,
    tables: props.tables,
  }),

  validate: values => validateTicket(values),

  handleSubmit: (
    { selectedDay, selectedGroup, selectedTime, ...values },
    { props, setSubmitting, setTouched, resetForm, setFieldValue },
  ) => {
    const { isSeatMap, isSeatsIo } = values
    delete values.isSeatMap

    props
      .onSubmit({ ...values }, isSeatMap, isSeatsIo)
      .then(() => {
        setTouched(false)
        setSubmitting(false)
        resetForm()
        if (isSeatsIo && isSeatMap) {
          setFieldValue('ticketHolders', [])
          setFieldValue('isSeatsIo', values.isSeatsIo)
        }
      })
      .catch(err => {
        setTouched(false)
        setSubmitting(false)
      })
  },
})(GuestTicketForm)
